/* IraqFlag — production-grade animated Iraqi national flag (v3).

   Fixes from v2:
   - Arabic text "الله أكبر" is rendered as an HTML overlay (dir=rtl, lang=ar)
     so the browser handles Arabic shaping/joining correctly. SVG <text>
     was breaking ligatures on iOS/Safari.
   - Softer displacement on the stripes — keeps the cloth wave but kills
     the jagged pixel edges that appeared in v2.
   - Cleaner perspective swing + animated cloth edge.
   - Light "shimmer wave" on the text so it ripples with the cloth without
     becoming unreadable.
*/
export default function IraqFlag({ className = "w-14 h-9", testid = "iraq-flag" }) {
  const uid = "iqflag";
  return (
    <span
      data-testid={testid}
      aria-label="علم العراق"
      role="img"
      className={`relative inline-block ${className}`}
      style={{ perspective: "260px" }}
    >
      {/* Cloth wrapper — perspective swing */}
      <span
        className="relative block w-full h-full rounded-[1.5px] overflow-hidden"
        style={{
          transformOrigin: "right center",
          transformStyle: "preserve-3d",
          animation: "iq-swing 4.2s ease-in-out infinite",
          filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.25))",
        }}
      >
        {/* SVG layer — colour stripes + cloth wave (no text here) */}
        <svg
          viewBox="0 0 300 200"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="absolute inset-0 block w-full h-full"
        >
          <defs>
            <filter id={`${uid}-wave`} x="-6%" y="-10%" width="112%" height="120%">
              <feTurbulence type="turbulence" baseFrequency="0.013 0.032" numOctaves="2" seed="3">
                <animate attributeName="seed" values="3;8;13;18;23;3" dur="3.8s" repeatCount="indefinite" />
              </feTurbulence>
              <feDisplacementMap in="SourceGraphic" scale="5" />
            </filter>
            <clipPath id={`${uid}-edge`}>
              <path>
                <animate
                  attributeName="d"
                  dur="3.8s"
                  repeatCount="indefinite"
                  values="
                    M0,3 Q75,-1 150,3 T300,3 L300,197 Q225,201 150,197 T0,197 Z;
                    M0,1 Q75,5 150,1 T300,4 L300,199 Q225,195 150,199 T0,196 Z;
                    M0,4 Q75,0 150,4 T300,1 L300,196 Q225,200 150,196 T0,199 Z;
                    M0,3 Q75,-1 150,3 T300,3 L300,197 Q225,201 150,197 T0,197 Z
                  "
                />
              </path>
            </clipPath>
            <linearGradient id={`${uid}-folds`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"  stopColor="rgba(0,0,0,0.16)" />
              <stop offset="22%" stopColor="rgba(255,255,255,0.10)" />
              <stop offset="48%" stopColor="rgba(0,0,0,0.12)" />
              <stop offset="74%" stopColor="rgba(255,255,255,0.08)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.14)" />
            </linearGradient>
          </defs>

          <g clipPath={`url(#${uid}-edge)`}>
            <g filter={`url(#${uid}-wave)`}>
              <rect x="0" y="0"      width="300" height="66.66" fill="#CE1126" />
              <rect x="0" y="66.66"  width="300" height="66.66" fill="#FFFFFF" />
              <rect x="0" y="133.33" width="300" height="66.67" fill="#000000" />
            </g>
            <rect x="0" y="0" width="300" height="200" fill={`url(#${uid}-folds)`}>
              <animate attributeName="x" values="-35;25;-35" dur="3.8s" repeatCount="indefinite" />
            </rect>
          </g>
        </svg>

        {/* HTML text overlay — browser handles Arabic shaping correctly */}
        <span
          dir="rtl"
          lang="ar"
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          style={{
            fontFamily: "'Reem Kufi', 'Amiri', 'Tajawal', serif",
            fontWeight: 800,
            color: "#007A3D",
            fontSize: "44%",
            lineHeight: 1,
            letterSpacing: "0.06em",
            textShadow: "0 0 0.5px rgba(0,122,61,0.6)",
            animation: "iq-text-ripple 3.8s ease-in-out infinite",
          }}
        >
          الله أكبر
        </span>

        {/* Specular moving highlight — adds cloth depth */}
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(108deg, rgba(255,255,255,0) 32%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 66%)",
            backgroundSize: "230% 100%",
            animation: "iq-shine 4.2s ease-in-out infinite",
            mixBlendMode: "soft-light",
          }}
        />
      </span>

      <style>{`
        @keyframes iq-swing {
          0%, 100% { transform: rotateY(-12deg) rotateX(2deg) skewY(-1deg); }
          50%      { transform: rotateY(  9deg) rotateX(-1deg) skewY( 1deg); }
        }
        @keyframes iq-shine {
          0%, 100% { background-position: 140% 0; }
          50%      { background-position: -40% 0; }
        }
        @keyframes iq-text-ripple {
          0%, 100% { transform: translateY(0) skewX(-3deg) scaleX(1.02); }
          50%      { transform: translateY(0.3px) skewX( 2deg) scaleX(0.98); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="${testid}"] *,
          [data-testid="${testid}"]   { animation: none !important; }
        }
      `}</style>
    </span>
  );
}
